import { UserService } from 'src/app/core/services/open-api/user.service';
import { Component, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { MenuItem } from 'primeng/api';
import { ActivatedRoute, Router } from '@angular/router';
import { License, LicenseState, TrialState } from 'src/app/core/services/utils/license-state';
import { TranslateService } from '@ngx-translate/core';
import { CurrentUser } from 'src/app/core/models/user';
import { UserPermission } from 'src/app/core/models/user-permission';
import { LoadingService } from 'src/app/core/services/loading.service';
import { CrmBody, CrmWebhookEvent, CrmWebhookService } from 'src/app/core/services/crm-webhook.service';
import { LocalStorageService } from 'src/app/core/services/utils/local-storage.service';

@Component({
    selector: 'app-trial-top-bar',
    templateUrl: './trial-top-bar.component.html',
    // styleUrls: ['./trial-top-bar.component.scss'],
    standalone: false,
})
export class TrialTopBarComponent implements OnDestroy {
    stepItems: MenuItem[] = [];
    name?: string;
    currentUser?: CurrentUser;
    public isPro = false;
    allowedToManageSubscription = false;
    hasApiKey = false;
    trialState?: TrialState;
    trialDaysLeft: number = 0;
    displayModalStartTrial: boolean = false;
    displayModalTrialStarted: boolean = false;
    displayModalTryHardware: boolean = false;
    displayModalTrialEnded: boolean = false;
    displayStartTrialButton: boolean = false;
    activeIndex: number = 0;
    dialogPage: number = 0;
    private subscriptions = new Subscription();

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private readonly licenseState: LicenseState,
        private readonly translate: TranslateService,
        private readonly userService: UserService,
        private readonly loadingService: LoadingService,
        private readonly crmWebhookService: CrmWebhookService,
    ) {
        this.subscriptions.add(
            this.userService.currentUser$.subscribe((user) => {
                if (user) {
                    this.name = user.firstName + ' ' + user.lastName;
                    this.isPro = user.isPro();
                }
            }),
        );
    }

    private updateWithCurrentUser(currentUser: CurrentUser): void {
        this.currentUser = currentUser;
        this.isPro = currentUser.isPro();
        this.allowedToManageSubscription = currentUser.hasPermission(UserPermission.MANAGE_SUBSCRIPTION);
        this.hasApiKey = currentUser.settings.apiKey !== null;
    }

    ngOnInit(): void {
        this.displayStartTrialButton = this.route.snapshot.queryParamMap.get('firstStart') !== 'true';

        this.subscriptions.add(
            this.userService.loadCurrentUser().subscribe((currentUser: CurrentUser) => {
                this.updateWithCurrentUser(currentUser);
                this.load();
            }),
        );

        this.translate
            .get(['dialog_starttrial_step_buzzer', 'dialog_starttrial_step_trial'])
            .subscribe((translations) => {
                this.stepItems = [
                    {
                        label: translations.dialog_starttrial_step_buzzer,
                    },
                    {
                        label: translations.dialog_starttrial_step_trial,
                    },
                ];
            });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    nextStep(): void {
        if (this.dialogPage + 1 === 1) {
            LocalStorageService.getTrialStep();
            // When 30-day trial selected
            if (LocalStorageService.getTrialStep() === null) {
                LocalStorageService.storeTrialStep('30days');
            }
            this.goToBuyBuzzer(1, false);
        } else {
            this.activeIndex++;
            this.dialogPage++;
        }
    }

    skipStep(): void {
        this.activeIndex++;
        this.dialogPage = 2;
    }

    previousStep(): void {
        this.activeIndex--;
        this.dialogPage = 0;
    }

    startTrial(days: number): void {
        const trialDate = new Date();
        trialDate.setDate(trialDate.getDate() + days);
        const dateToISO = trialDate.toISOString();

        this.loadingService.setLoading(true);

        if (days === 30) {
            LocalStorageService.removeTrialStep();
        }

        this.userService.startTrial(dateToISO).subscribe(() => {
            const crmContent: CrmBody = {
                email: this.currentUser?.email,
            };

            if (days === 30) {
                void this.crmWebhookService.send(CrmWebhookEvent.startTrial30days, crmContent);
            } else if (days === 15) {
                void this.crmWebhookService.send(CrmWebhookEvent.startTrial15days, crmContent);
            }

            this.subscriptions.add(
                this.userService.loadCurrentUser(true).subscribe((user: CurrentUser) => {
                    this.updateWithCurrentUser(user);
                    this.loadingService.setLoading(false);
                    this.displayModalStartTrial = false;
                    this.displayModalTrialStarted = true;
                }),
            );
        });
    }

    load(): void {
        this.licenseState.getLicense().subscribe((license: License) => {
            if (this.allowedToManageSubscription) {
                this.trialState = license.feature.trial.trialState;
                this.trialDaysLeft = license.feature.trial.trialDaysLeft;

                if (this.trialState === TrialState.EXPIRED && !this.isPro) {
                    // Workaround: APIKey wid beim Downgrade gelöscht, solange die Trial abgelaufne ist und es einen APIKey gibt wird der "Trial ended" Dialog angezegit
                    // TODO: weiteres Datenfeld trialState in API einbauen, da könnte man auch speichern wenn die trial nochmal um 10 TAge verlängert werden soll
                    if (this.hasApiKey) {
                        this.displayModalTrialEnded = true;
                    }
                }

                if (this.trialState === TrialState.UNUSED && !this.isPro) {
                    if (!this.isTrialActive() && !this.isTrialExpired()) {
                        if (LocalStorageService.getTrialStep() === '30days') {
                            this.dialogPage = 1;
                            this.activeIndex++;
                        }
                        this.displayModalStartTrial = true;
                    }
                }
            }
        });
    }

    linkToStore(): void {
        void this.router.navigateByUrl('/settings/store');
    }

    openModalTryHardware(): void {
        this.displayModalTrialStarted = false;
        this.displayModalTryHardware = true;
    }

    trialEndedDowngrade(): void {
        this.displayModalTrialEnded = false;

        this.loadingService.setLoading(true);

        // delete ApiKey
        this.userService.deleteAPIKey().subscribe(() => {
            this.loadingService.setLoading(false);
            void this.router.navigateByUrl('/settings/store');
        });
    }

    trialEndedSales(): void {
        this.translate.get(['dialog_trialended_button_sales_link']).subscribe((translations) => {
            window.open(translations.dialog_trialended_button_sales_link, '_blank');
        });
    }

    trialEndedUpgrade(): void {
        this.displayModalTrialEnded = false;
        void this.goToBuyLicence(1);
    }

    goToBuyBuzzer(addBuzzerQuantity: number, newtab: boolean): void {
        this.translate
            .get(['language_parameter_store_link', 'addons_hardware_woocommerce_product_id'])
            .subscribe((translations) => {
                this.userService.getLicensesURL().subscribe((data) => {
                    const domain = data.split('?');
                    const newUrl =
                        domain[0] +
                        translations.language_parameter_store_link +
                        '/cart' +
                        (addBuzzerQuantity !== 0
                            ? '?' +
                              'add-to-cart=' +
                              translations.addons_hardware_woocommerce_product_id +
                              '&quantity=' +
                              addBuzzerQuantity +
                              '&'
                            : '?') +
                        domain[1];
                    const win = window.open(newUrl, newtab ? '_blank' : '_self');
                    if (!win || win.closed || typeof win.closed == 'undefined') {
                        console.log('Popup blocked');
                    }
                });
            });
    }

    goToBuyLicence(addLicencesQuantity: number): void {
        this.translate
            .get(['language_parameter_store_link', 'subscription_pro_plan_woocommerce_product_id'])
            .subscribe((translations) => {
                this.userService.getLicensesURL().subscribe((data) => {
                    // console.debug("data: " + data)
                    const domain = data.split('?');
                    const newUrl =
                        domain[0] +
                        translations.language_parameter_store_link +
                        '/cart' +
                        '?' +
                        'add-to-cart=' +
                        translations.subscription_pro_plan_woocommerce_product_id +
                        '&quantity=' +
                        addLicencesQuantity +
                        '&' +
                        domain[1]; // "&quantity=" + quantity + "&" + domain[1]
                    // console.debug("newUrl: " + newUrl)
                    window.open(newUrl, '_self'); //  _self  _blank
                });
            });
    }

    isTrialActive(): boolean {
        return this.trialState === TrialState.ACTIVE;
    }

    isTrialExpired(): boolean {
        return this.trialState === TrialState.EXPIRED && !this.isPro;
    }

    displayTrialButtonStart(): boolean {
        return this.trialState === TrialState.UNUSED && !this.isPro;
    }
}
